import type { MaguchiFirstCategory, UomType } from './item.type'
import type { PreferenceType } from './sales-matrix.type'

// 顧客の最新 sales matrix が対象。matrix 未作成なら全て 0
export type MatrixRowCount = Record<PreferenceType, number>

export type CustomerSummary = {
  customerId: number
  accountNumber: number
  accountName: string
}

// 集計レスポンスが返すまぐちカテゴリの識別情報。
// item.type の MaguchiCategory（code / name）とは field 名が違う別形。
export type MaguchiCategorySummary = {
  maguchiCategoryCode: number
  maguchiCategoryName: string
  maguchiFirstCategory: MaguchiFirstCategory
}

export type AggregateGranularity = 'weekly' | 'monthly'

export type PeriodData = {
  periodIndex: number
  periodStart: Date
  periodEnd: Date
  periodLabel: string
}

// --- customer-maguchi-category 軸（実績 = Shipped 以降のみ） ---

export type AggregatesCustomerMaguchiCategoryQuery = {
  customerId: number
  granularity?: AggregateGranularity
  periods?: number
  until?: string // YYYY-MM-DD
}

// primaryShippedQty は item ごとに primary UOM が違うため、複数 item をまたぐ集計は
// 単位ごとに分けないと足し合わせが成立しない
export type ShippedQtyByUom = Record<UomType, number>

export type MaguchiCategoryAggregate = MaguchiCategorySummary & {
  totalPrimaryShippedQty: ShippedQtyByUom
}

export type AggregatesCustomerMaguchiCategoryResult = {
  customerId: number
  granularity: AggregateGranularity
  periods: Array<
    PeriodData & {
      aggregates: {
        byMaguchiFirstCategory: Array<{
          category: MaguchiFirstCategory
          uniqueCategoryCount: number
        }>
        byMaguchiCategory: MaguchiCategoryAggregate[]
      }
    }
  >
}

export type AggregatesCustomerMaguchiCategoryByMaguchiCategoryCodeQuery = {
  maguchiCategoryCode: number
  customerId: number
  granularity?: AggregateGranularity
  periods?: number
  until?: string // YYYY-MM-DD
}

export type ItemAggregate = {
  itemId: number
  itemNumber: string
  itemName: string
  // item の primary UOM。totalPrimaryShippedQty の単位。明細の発注単位とは別物
  primaryUom: UomType
  totalPrimaryShippedQty: number // 出荷が無ければ 0
}

// 対象カテゴリの全 item が常に並ぶ（期間内に出荷が無い item は totalPrimaryShippedQty: 0）
export type AggregatesCustomerMaguchiCategoryByMaguchiCategoryCodeResult = {
  customerId: number
  granularity: AggregateGranularity
  maguchiCategory: MaguchiCategorySummary
  periods: Array<PeriodData & { items: ItemAggregate[] }>
}

// --- monthly-changes 軸（実績 = Shipped 以降のみ） ---

export const MonthlyChangeDirection = {
  increased: 'increased',
  decreased: 'decreased',
  unchanged: 'unchanged',
} as const
export type MonthlyChangeDirection = keyof typeof MonthlyChangeDirection

export type MonthlyChangeComparison = {
  direction: MonthlyChangeDirection
  delta: number
}

// サーバーが months[].changeFromPrev を作るのと同じ算出。行を合算した値や 0 埋めした月の
// 増減はサーバーから返らないので、クライアント側で同じ規則に揃えて出す
export const compareMonthlyChange = (
  current: number,
  previous: number,
): MonthlyChangeComparison => {
  const delta = current - previous
  return {
    direction:
      delta > 0
        ? MonthlyChangeDirection.increased
        : delta < 0
          ? MonthlyChangeDirection.decreased
          : MonthlyChangeDirection.unchanged,
    delta,
  }
}

// 返却された nextCursor を cursor に渡して呼び直すと 1 つ古いウィンドウが取れる
export type MonthlyWindowQuery = {
  cursor?: string // YYYY-MM。この月を含めて古い方へ months ヶ月
  months?: number // 1〜24。省略時は 3
}

export type MonthlyWindowResult = {
  yearMonths: string[] // 新→旧
  nextCursor: string
}

export type MonthlyCount = {
  yearMonth: string
  changeFromPrev: MonthlyChangeComparison // 1 つ古い月に対する増減
}

export type MonthlyCustomerCount = MonthlyCount & { customerCount: number }

export type MonthlyCategoryCount = MonthlyCount & { categoryCount: number }

// --- by-maguchi-category 軸 ---

export type MonthlyChangesByMaguchiCategoryQuery = MonthlyWindowQuery & {
  targetSalesrepNumber?: number
}

export type MaguchiCategoryMonthlyChange = MaguchiCategorySummary & {
  months: MonthlyCustomerCount[] // yearMonths と同じ並び・同じ長さ
}

export type MonthlyChangesByMaguchiCategoryResult = MonthlyWindowResult & {
  targetSalesrepNumber: number | null
  categories: MaguchiCategoryMonthlyChange[] // 全カテゴリがカテゴリコード昇順で並ぶ
}

export type CustomerMonthlyChange = CustomerSummary & {
  months: MonthlyCustomerCount[] // その月に該当カテゴリを注文したか（顧客数 0/1）
}

export type MonthlyChangesByMaguchiCategoryCodeResult = MonthlyWindowResult & {
  targetSalesrepNumber: number | null
  maguchiCategory: MaguchiCategorySummary
  customers: CustomerMonthlyChange[] // ウィンドウ内に注文のある顧客のみ（顧客ID昇順）
}

// --- by-customer 軸 ---

export type MonthlyChangesByCustomerQuery = MonthlyWindowQuery & {
  targetSalesrepNumber: number
}

export type CustomerCategoryMonthlyChange = CustomerSummary & {
  months: MonthlyCategoryCount[]
  matrixRowCount: MatrixRowCount
}

export type NoOrderCustomer = CustomerSummary & {
  lastOrderedDate: Date | null
}

export type MonthlyChangesByCustomerResult = MonthlyWindowResult & {
  targetSalesrepNumber: number
  customers: CustomerCategoryMonthlyChange[] // ウィンドウ内に注文のある顧客（顧客ID昇順）
  noOrderCustomers: NoOrderCustomer[] // ウィンドウ内に注文が全くない顧客（顧客ID昇順）
}

export type MaguchiCategoryOrderMonthlyChange = MaguchiCategorySummary & {
  months: MonthlyCategoryCount[] // その月にそのカテゴリを注文したか（カテゴリ数 0/1）
}

export type MonthlyChangesByCustomerIdResult = MonthlyWindowResult & {
  customer: CustomerSummary
  categories: MaguchiCategoryOrderMonthlyChange[] // ウィンドウ内に注文したカテゴリのみ
  matrixId: number | null
  matrixRowCount: MatrixRowCount
}

// --- preliminary 軸（速報 = 未出荷の注文。月で範囲を切らず増減も返さない） ---

export type PreliminaryByMaguchiCategoryQuery = {
  targetSalesrepNumber?: number
}

export type PreliminaryByCustomerQuery = {
  targetSalesrepNumber: number
}

export type PreliminaryByMaguchiCategoryResult = {
  targetSalesrepNumber: number | null
  asOf: Date // この時刻時点で未出荷の注文を集計している
  months: Array<{
    yearMonth: string // 注文月
    categories: Array<MaguchiCategorySummary & { customerCount: number }>
  }> // 新→旧。先月以前の月が残っていれば出荷の滞留を意味する
}

export type PreliminaryByMaguchiCategoryCodeQuery = PreliminaryByMaguchiCategoryQuery & {
  maguchiCategoryCode: number
}

// by-maguchi-category の customerCount の内訳
export type PreliminaryByMaguchiCategoryCodeResult = {
  targetSalesrepNumber: number | null
  maguchiCategory: MaguchiCategorySummary
  asOf: Date
  months: Array<{
    yearMonth: string
    customers: CustomerSummary[]
  }>
}

export type PreliminaryByCustomerResult = {
  targetSalesrepNumber: number
  asOf: Date
  months: Array<{
    yearMonth: string
    customers: Array<CustomerSummary & { categoryCount: number }>
  }>
}

// by-customer の categoryCount の内訳
export type PreliminaryByCustomerIdResult = {
  customer: CustomerSummary
  asOf: Date
  months: Array<{
    yearMonth: string
    categories: MaguchiCategorySummary[]
  }>
}

// --- sales 集計軸（billedTotalAmount 合計・注文日基準・実績のみ） ---

export type MonthlySales = {
  yearMonth: string
  sales: number
}

export type MonthlySalesWithComparison = MonthlySales & {
  momPercent: number | null
  yoyPercent: number | null
}

export type AggregatesSalesQuery = {
  targetSalesrepNumber: number
  until?: string // YYYY-MM-DD
}

export type AggregatesSalesResult = {
  targetSalesrepNumber: number
  currentMonth: MonthlySales // 金額は出荷後に確定するため後から増える
  lastMonth: MonthlySalesWithComparison
}

export type AggregatesSalesByCustomerQuery = {
  customerId: number
  until?: string // YYYY-MM-DD
}

export type AggregatesSalesByCustomerResult = {
  customerId: number
  currentMonth: MonthlySales
  lastMonth: MonthlySalesWithComparison
}

// --- gross margin 軸（COGS と粗利。実績 = Shipped のみ・注文日基準） ---

export type GrossMarginQuery = {
  targetSalesrepNumber: number
  month?: string // YYYY-MM。集計する 1 か月。省略時はサーバーの基準月
}

// 粗利率の分母は sales ではなく salesCosted（原価を当てられた売上）。
// sales を分母にすると原価未計上のぶんだけ粗利が膨らむ
export type GrossMarginFigures = {
  sales: number
  cogs: number
  salesCosted: number
  grossProfit: number
  grossMarginPercent: number | null // 0〜100（小数第1位）。salesCosted が 0 なら null
}

// 末尾の 2 つは原価を当てられなかった売上の内訳。sales = salesCosted + unresolvedItemSales + noCostSales
type GrossMarginSummary = GrossMarginFigures & {
  unresolvedItemSales: number // itemId を解決できなかった明細
  noCostSales: number // item は分かるが使える原価が無い明細
}

export type GrossMarginItemRow = GrossMarginFigures &
  MaguchiCategorySummary & {
    itemId: number
    itemNumber: string
    itemName: string
    // 商品マスタは支店ごとの行。担当は複数支店に顧客を持ちうるので、同じ品番が支店の数だけ別原価で並ぶ
    branchOrg: string // 支店コード（例: HOU）
    primaryUom: UomType // shippedQty の単位
    shippedQty: number
    freeShippedQty: number // shippedQty のうち $0 で出荷した数量（deal の無償分・サンプル）
    unitCost: number | null // primaryUom あたりの平均原価。使える原価データが無ければ null
  }

export type GrossMarginMaguchiCategoryRow = MaguchiCategorySummary & GrossMarginFigures

export type GrossMarginCustomerRow = CustomerSummary & GrossMarginFigures

type GrossMarginBreakdownResult = {
  targetSalesrepNumber: number
  yearMonth: string // YYYY-MM。month を省略したときはサーバーが決めた基準月
}

// rows は sales 降順
export type GrossMarginItemsResult = GrossMarginBreakdownResult & {
  rows: GrossMarginItemRow[]
}

// item を解決できない明細はカテゴリに join できず rows に乗らない
export type GrossMarginMaguchiCategoriesResult = GrossMarginBreakdownResult & {
  rows: GrossMarginMaguchiCategoryRow[]
}

export type GrossMarginCustomersResult = GrossMarginBreakdownResult & {
  rows: GrossMarginCustomerRow[]
}

type GrossMarginMonth = GrossMarginSummary & {
  yearMonth: string
}

// currentMonth は month の月（省略時はサーバーの基準月）、lastMonth はその前月
export type GrossMarginResult = {
  targetSalesrepNumber: number
  currentMonth: GrossMarginMonth
  lastMonth: GrossMarginMonth
}

// grossMarginPercent は 0〜100 で返るので、0〜1 を前提にする formatPercent は使えない
export const formatGrossMarginPercent = (percent: number): string => `${percent.toFixed(1)}%`

// --- URL クエリパラメータの相互変換 ---

// TODO(temporary): order_history の集計精度が上がったら削除する
export const SalesProfitSource = {
  dsr: 'dsr',
  orders: 'orders',
} as const
export type SalesProfitSource = keyof typeof SalesProfitSource

export type SalesProfitFilters = {
  salesrepNumber?: number
  month?: string // YYYY-MM
  source: SalesProfitSource
}

export type SalesProfitRouteQuery = {
  salesrep_number?: string
  month?: string
  source: SalesProfitSource
}

export const parseSalesProfitQuery = (params: Record<string, unknown>): SalesProfitFilters => {
  const salesrepNumber = Number(params.salesrep_number)
  const month = String(params.month ?? '')
  return {
    salesrepNumber:
      Number.isInteger(salesrepNumber) && salesrepNumber > 0 ? salesrepNumber : undefined,
    month: /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : undefined,
    source:
      params.source === SalesProfitSource.orders ? SalesProfitSource.orders : SalesProfitSource.dsr,
  }
}

export const buildSalesProfitQuery = (filters: SalesProfitFilters): SalesProfitRouteQuery => {
  const result: SalesProfitRouteQuery = { source: filters.source }
  if (filters.salesrepNumber !== undefined) {
    result.salesrep_number = String(filters.salesrepNumber)
  }
  if (filters.month !== undefined) {
    result.month = filters.month
  }
  return result
}
